import { forwardRef, useImperativeHandle } from 'react';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Webcam from 'react-webcam';

import _noop from '@julofinance/web-helpers/dist/fn/noop';
import callAllFn from '@julofinance/web-helpers/dist/fn/callAllFn';

import PhotoUploadGuidance from 'components/bottomsheets/PhotoUploadGuidanceReUpload';
import { useWindowSize } from 'hooks/use-window-size';
import circle from 'assets/circle.png';
import leftArrow from 'assets/ic_back.png';

import useGetCameraFrame from './usecase/use-get-camera-frame';
import useGetCameraConstraints from './usecase/use-get-camera-constraints';

import Button from '../button';
import { cameraContainer } from './styles';
import { CameraProps, CameraRef } from './types';
import useCameraAction from './usecase/use-camera-action';
import useCameraGuidance from './usecase/use-camera-guidance';

const Camera = forwardRef<CameraRef, CameraProps>((props, ref) => {
  const {
    onCameraCapture,
    onCameraClose = _noop,
    onCameraOpen = _noop,
    onCameraResultOK = _noop,
    onCameraReCapture,
    docType = 'ktp',
    cameraFacing = 'front',
    isLoading,
  } = props;

  const {
    onOpenCamera,
    onCloseCamera,
    handleCapture,
    webcamRef,
    isOpenCamera,
    onReCapture,
    imagePreview,
  } = useCameraAction({ onCameraClose, onCameraOpen });
  const { onCloseGuidance, onOpenGuidance, isShowGuidance } =
    useCameraGuidance();
  const [wWidth, wHeight] = useWindowSize();
  const cameraFrame = useGetCameraFrame({ cameraFacing, docType });
  const videoConstraints = useGetCameraConstraints({ cameraFacing });

  useImperativeHandle(
    ref,
    () => {
      return {
        openCamera: onOpenCamera,
        closeCamera: onCloseCamera,
      };
    },
    [onCloseCamera, onOpenCamera],
  );

  return (
    <Dialog open={isOpenCamera} onClose={onCloseCamera} fullScreen>
      <DialogContent className={cameraContainer}>
        <IconButton
          className='back-btn'
          href=''
          onClick={callAllFn(onCloseGuidance, onCloseCamera)}
        >
          <img src={leftArrow} />
        </IconButton>
        <div className='camera-wrapper'>
          {!imagePreview ? (
            <>
              <Webcam
                audio={false}
                mirrored={false}
                ref={webcamRef}
                width={wWidth}
                height={wHeight}
                screenshotFormat='image/jpeg'
                videoConstraints={videoConstraints}
                forceScreenshotSourceSize
              />
              <div className='camera-placeholder'>{cameraFrame}</div>
            </>
          ) : (
            <img src={imagePreview} className='camera-result' />
          )}
        </div>
        <div className='camera-trigger-container'>
          {!Boolean(imagePreview) ? (
            <IconButton
              className='camera-trigger'
              onClick={handleCapture(onCameraCapture)}
            >
              <img src={circle} />
            </IconButton>
          ) : (
            <>
              <Button
                className='camera-trigger-retry'
                variant='tertiary'
                onClick={callAllFn(onReCapture, onCameraReCapture)}
                disabled={isLoading}
              >
                Foto Ulang
              </Button>
              <Button
                disabled={isLoading}
                className='ok'
                variant='primary'
                onClick={() => onCameraResultOK(imagePreview)}
              >
                Ok
              </Button>
            </>
          )}
        </div>
        <Button
          variant='tertiary'
          className='upload-guide-btn'
          onClick={onOpenGuidance}
          fullWidth
        >
          Pentunjuk upload
        </Button>
        <PhotoUploadGuidance
          anchor='bottom'
          open={isShowGuidance}
          onClose={onCloseGuidance}
        />
      </DialogContent>
    </Dialog>
  );
});

export default Camera;
